import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ModuleAccessLockIcon from "@/components/ModuleAccessLockIcon";

/** Máximo de viñetas en tarjetas del Panel General (hub principal). */
export const HUB_CARD_MAX_BULLETS = 5;

/**
 * Tarjeta del Panel General (hub principal) — entrada a un módulo completo.
 */
export default function HubModuleCard({
  meta,
  icon: Icon,
  hasModuleAccess,
  accessLoading,
  onEnter,
  testId,
  actionLabel = "Entrar al módulo",
  actionDisabled = false,
  actionVariant = "primary",
  pendingNote = null,
}) {
  const bullets = (meta.bullets || []).slice(0, HUB_CARD_MAX_BULLETS);
  const isPending = actionVariant === "pending";
  return (
    <article
      className="relative flex flex-col rounded-sm border border-slate-200 bg-white p-5 min-h-[240px] transition-shadow hover:shadow-md hover:border-[#0F2D5C]/20"
      data-testid={testId}
      data-module-access={accessLoading ? "loading" : hasModuleAccess ? "granted" : "limited"}
      data-module-action={isPending ? "pending" : hasModuleAccess ? "enter" : actionVariant === "request" ? "request" : "enter"}
    >
      <ModuleAccessLockIcon hasModuleAccess={hasModuleAccess} loading={accessLoading} />
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2.5 rounded-sm bg-[#0F2D5C]/10 text-[#0F2D5C] shrink-0">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 pr-6">
          <h3 className="text-base font-bold text-[#0F2D5C] leading-tight">{meta.title}</h3>
          <p className="text-[11px] text-slate-600 mt-1 leading-snug">{meta.description}</p>
        </div>
      </div>
      {bullets.length > 0 ? (
        <ul className="text-xs text-slate-700 space-y-1.5 mb-4 flex-1 list-disc pl-4 marker:text-[#3DA873]">
          {bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      ) : (
        <div className="flex-1" />
      )}
      {pendingNote ? (
        <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 mb-2 m-0">
          {pendingNote}
        </p>
      ) : null}
      <Button
        type="button"
        onClick={onEnter}
        disabled={actionDisabled}
        variant={actionVariant === "request" ? "outline" : "default"}
        className={`w-full rounded-sm h-10 text-sm font-semibold mt-auto ${
          actionVariant === "request"
            ? "border-[#0F2D5C] text-[#0F2D5C] hover:bg-[#0F2D5C]/5"
            : isPending
              ? "bg-amber-100 text-amber-950 hover:bg-amber-100 border border-amber-300"
              : "bg-[#0F2D5C] hover:bg-[#0a2248]"
        }`}
        data-testid={`${testId}-enter`}
      >
        {actionLabel}
        {!isPending ? <ArrowRight className="w-4 h-4 ml-2" /> : null}
      </Button>
    </article>
  );
}

/**
 * Tarjeta de función dentro del hub de inicio de un módulo (navegación híbrida).
 */
export function ModuleFeatureCard({
  label,
  description,
  icon: Icon,
  hasModuleAccess,
  accessLoading,
  onEnter,
  navStep,
  pending,
  testId,
  cardCategory,
  cardStatus,
}) {
  return (
    <article
      className="relative flex flex-col rounded-sm border border-slate-200 bg-white p-4 min-h-[140px] transition-shadow hover:shadow-md hover:border-[#0F2D5C]/20"
      data-testid={testId}
      data-module-access={accessLoading ? "loading" : hasModuleAccess ? "granted" : "limited"}
      {...(cardCategory ? { "data-card-category": cardCategory } : {})}
      {...(cardStatus ? { "data-card-status": cardStatus } : {})}
    >
      <ModuleAccessLockIcon hasModuleAccess={hasModuleAccess} loading={accessLoading} className="top-3 right-3" />
      <div className="flex items-start gap-3 mb-3 flex-1">
        <div className="p-2 rounded-sm bg-[#0F2D5C]/10 text-[#0F2D5C] shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 pr-6">
          <h3 className="text-sm font-semibold text-[#0F2D5C] leading-snug">{label}</h3>
          {description ? (
            <p className="text-[11px] text-slate-600 mt-1 leading-snug line-clamp-3">{description}</p>
          ) : null}
          {pending ? (
            <p className="text-[10px] text-amber-700 mt-1">En desarrollo</p>
          ) : null}
          {navStep ? (
            <span className="inline-block mt-1 text-[9px] font-bold tabular-nums rounded px-1.5 py-0.5 bg-slate-100 text-slate-500">
              Paso {navStep}
            </span>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        onClick={onEnter}
        className="w-full rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248] h-9 text-xs font-semibold mt-auto"
        data-testid={`${testId}-enter`}
      >
        Abrir
        <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
      </Button>
    </article>
  );
}
